import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ApiError,
  dens as densApi,
  events as eventsApi,
  meetings as meetingsApi,
  type CalendarEvent,
  type DenRow,
  type EventDetail,
  type EventInput,
  type MeetingListItem,
  type RsvpResponse,
} from '../api';
import { useUnits } from '../units';

type Row =
  | { kind: 'event'; when: number; ev: CalendarEvent }
  | { kind: 'meeting'; when: number; mt: MeetingListItem };

function fmt(d: Date) {
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtTime(d: Date) {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function Calendar() {
  const { unitId = '' } = useParams();
  const { currentUnit, hasRole } = useUnits();
  const canManage = hasRole('ADMIN') || hasRole('DEN_LEADER');
  const seesMeetings = hasRole('ADMIN') || hasRole('DEN_LEADER');

  const [evs, setEvs] = useState<CalendarEvent[]>([]);
  const [mts, setMts] = useState<MeetingListItem[]>([]);
  const [dens, setDens] = useState<DenRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    eventsApi
      .list(unitId, 'upcoming')
      .then(setEvs)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load the calendar'));
    if (seesMeetings) {
      meetingsApi.list(unitId, 'upcoming').then(setMts).catch(() => setMts([]));
      densApi.list(unitId).then((r) => setDens(r.dens)).catch(() => setDens([]));
    }
  }, [unitId, seesMeetings]);

  useEffect(load, [load]);

  const rows = useMemo<Row[]>(() => {
    const r: Row[] = [
      ...evs.map((ev) => ({ kind: 'event' as const, when: Date.parse(ev.startsAt), ev })),
      ...mts.map((mt) => ({
        kind: 'meeting' as const,
        when: Date.parse(`${mt.date}T${mt.startTime ?? '00:00'}`),
        mt,
      })),
    ];
    return r.sort((a, b) => a.when - b.when);
  }, [evs, mts]);

  if (!currentUnit) return <Navigate to="/" replace />;

  return (
    <div className="panel">
      <h1>Calendar</h1>
      <p className="muted">{currentUnit.name} · upcoming</p>
      {error && <p className="error">{error}</p>}

      {canManage && <NewEventForm unitId={unitId} dens={dens} isAdmin={hasRole('ADMIN')} onCreated={load} />}

      {rows.length === 0 ? (
        <p className="muted">Nothing on the calendar yet.</p>
      ) : (
        <ul className="cal-list">
          {rows.map((row) =>
            row.kind === 'meeting' ? (
              <li key={`m-${row.mt.id}`} className="cal-item cal-meeting">
                <div className="cal-when">
                  <span>{fmt(new Date(row.when))}</span>
                  {row.mt.startTime && <span className="muted small">{row.mt.startTime}</span>}
                </div>
                <div className="cal-body">
                  <span className="badge cal-tag-meeting">Den meeting</span>{' '}
                  <Link to={`/units/${unitId}/meetings/${row.mt.id}`}>
                    {row.mt.title ?? `${row.mt.denName} meeting`}
                  </Link>
                  <div className="muted small">
                    {row.mt.denName}
                    {row.mt.adventures.length > 0 && ` · ${row.mt.adventures.join(', ')}`}
                  </div>
                </div>
              </li>
            ) : (
              <EventItem
                key={`e-${row.ev.id}`}
                unitId={unitId}
                ev={row.ev}
                canManage={canManage}
                dens={dens}
                isAdmin={hasRole('ADMIN')}
                onChanged={load}
              />
            ),
          )}
        </ul>
      )}
    </div>
  );
}

function EventItem({
  unitId,
  ev,
  canManage,
  dens,
  isAdmin,
  onChanged,
}: {
  unitId: string;
  ev: CalendarEvent;
  canManage: boolean;
  dens: DenRow[];
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const start = new Date(ev.startsAt);

  return (
    <li className="cal-item cal-event">
      <div className="cal-when">
        <span>{fmt(start)}</span>
        <span className="muted small">{fmtTime(start)}</span>
      </div>
      <div className="cal-body">
        <span className="badge cal-tag-event">{ev.scope === 'pack' ? 'Pack event' : `${ev.denName} event`}</span>{' '}
        <strong>{ev.title}</strong>
        {ev.rsvpRequired && <span className="muted small"> · RSVP requested</span>}
        {ev.capacity != null && <span className="muted small"> · capacity {ev.capacity}</span>}
        {ev.location && <div className="muted small">{ev.location}</div>}
        {ev.description && <div className="small">{ev.description}</div>}

        {!editing && <RsvpPanel unitId={unitId} eventId={ev.id} canManage={canManage} />}

        {canManage && !editing && (
          <div className="row" style={{ gap: 8, marginTop: 4 }}>
            <button className="linklike" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button
              className="linklike"
              onClick={async () => {
                if (confirm(`Delete "${ev.title}"?`)) {
                  await eventsApi.remove(unitId, ev.id);
                  onChanged();
                }
              }}
            >
              Delete
            </button>
          </div>
        )}
        {editing && (
          <NewEventForm
            unitId={unitId}
            dens={dens}
            isAdmin={isAdmin}
            existing={ev}
            onCreated={() => {
              setEditing(false);
              onChanged();
            }}
            onCancel={() => setEditing(false)}
          />
        )}
      </div>
    </li>
  );
}

const RESPONSES: RsvpResponse[] = ['yes', 'maybe', 'no'];

function RsvpPanel({
  unitId,
  eventId,
  canManage,
}: {
  unitId: string;
  eventId: string;
  canManage: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState<EventDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    if (open && !d) eventsApi.get(unitId, eventId).then(setD).catch(() => setD(null));
  }, [open, d, unitId, eventId]);

  async function respond(scoutId: string | null, response: RsvpResponse, current?: RsvpResponse) {
    setBusy(true);
    try {
      setD(
        current === response
          ? await eventsApi.clearRsvp(unitId, eventId, scoutId)
          : await eventsApi.rsvp(unitId, eventId, { scoutId, response }),
      );
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button className="linklike" style={{ marginTop: 4 }} onClick={() => setOpen(true)}>
        RSVP
      </button>
    );
  }

  const responders = d
    ? [{ id: null as string | null, name: 'You' }, ...d.eligibleScouts.map((s) => ({ id: s.id, name: s.name }))]
    : [];
  const lineFor = (scoutId: string | null) => d?.mine.find((m) => m.scoutId === scoutId);

  return (
    <div className="rsvp-panel">
      {!d && <p className="muted small">Loading…</p>}
      {d && (
        <>
          {d.capacity != null && (
            <p className="muted small">
              {d.counts.yes}/{d.capacity} going
              {d.counts.waitlisted > 0 && ` · ${d.counts.waitlisted} waitlisted`}
            </p>
          )}
          {responders.map((r) => {
            const line = lineFor(r.id);
            return (
              <div key={r.id ?? 'self'} className="rsvp-row">
                <span className="rsvp-who">{r.name}</span>
                <span className="rsvp-btns">
                  {RESPONSES.map((resp) => (
                    <button
                      key={resp}
                      disabled={busy}
                      className={line?.response === resp ? 'primary' : ''}
                      onClick={() => respond(r.id, resp, line?.response)}
                    >
                      {resp}
                    </button>
                  ))}
                </span>
                {line?.waitlisted && <span className="badge">waitlisted</span>}
              </div>
            );
          })}
          {responders.length === 1 && (
            <p className="muted small">Link a Scout to you (roster) to RSVP on their behalf.</p>
          )}

          {canManage && (
            <div style={{ marginTop: 6 }}>
              <button className="linklike" onClick={() => setShowList((s) => !s)}>
                {showList ? 'Hide' : 'Attendees'} ({d.counts.yes} yes
                {d.capacity != null ? ` / ${d.capacity}` : ''})
              </button>
              {showList && d.attendees && (
                <table className="grid" style={{ marginTop: 6 }}>
                  <tbody>
                    {d.attendees.map((a, i) => (
                      <tr key={i}>
                        <td>{a.userName}</td>
                        <td>{a.scoutName ?? <span className="muted">adult</span>}</td>
                        <td>
                          {a.response}
                          {a.waitlisted && ' (waitlisted)'}
                        </td>
                      </tr>
                    ))}
                    {d.attendees.length === 0 && (
                      <tr>
                        <td className="muted">No responses yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          )}
          <button className="linklike" style={{ marginTop: 4 }} onClick={() => setOpen(false)}>
            Close
          </button>
        </>
      )}
    </div>
  );
}

function toLocalInput(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function NewEventForm({
  unitId,
  dens,
  isAdmin,
  existing,
  onCreated,
  onCancel,
}: {
  unitId: string;
  dens: DenRow[];
  isAdmin: boolean;
  existing?: CalendarEvent;
  onCreated: () => void;
  onCancel?: () => void;
}) {
  const [title, setTitle] = useState(existing?.title ?? '');
  const [startsAt, setStartsAt] = useState(existing ? toLocalInput(existing.startsAt) : '');
  const [location, setLocation] = useState(existing?.location ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [denId, setDenId] = useState(existing?.denId ?? '');
  const [rsvpRequired, setRsvp] = useState(existing?.rsvpRequired ?? false);
  const [capacity, setCapacity] = useState(existing?.capacity != null ? String(existing.capacity) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body: EventInput = {
        title,
        startsAt: new Date(startsAt).toISOString(),
        location: location || null,
        description: description || null,
        denId: denId || null,
        rsvpRequired,
        capacity: capacity ? Number(capacity) : null,
      };
      if (existing) await eventsApi.update(unitId, existing.id, body);
      else await eventsApi.create(unitId, body);
      if (!existing) {
        setTitle('');
        setStartsAt('');
        setLocation('');
        setDescription('');
        setCapacity('');
      }
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the event');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="event-form" onSubmit={submit}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input placeholder="Event title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <input
          type="datetime-local"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
          required
          aria-label="Starts at"
        />
        <select value={denId} onChange={(e) => setDenId(e.target.value)}>
          <option value="">{isAdmin ? 'Whole pack' : 'Choose your den…'}</option>
          {dens.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </div>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input placeholder="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
        <label className="filter">
          <input type="checkbox" checked={rsvpRequired} onChange={(e) => setRsvp(e.target.checked)} /> RSVP
          requested
        </label>
        <input
          placeholder="Capacity"
          value={capacity}
          onChange={(e) => setCapacity(e.target.value.replace(/\D/g, ''))}
          style={{ minWidth: 90 }}
          inputMode="numeric"
        />
      </div>
      <textarea
        rows={2}
        placeholder="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div className="row" style={{ gap: 8 }}>
        <button type="submit" className="primary" disabled={busy || !title || !startsAt}>
          {existing ? 'Save event' : 'Add event'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
        {error && <span className="error">{error}</span>}
      </div>
    </form>
  );
}
